import cloneDeep from "lodash/cloneDeep";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import submit from "submit-form";
import { getEffectiveDefaultInsightsLens } from "../../../constants/default-insights-lens";
import { useRillContext } from "../../../rill/RillReact/share/base";
import type {
  BodyPartKey,
  ClientConfiguration as ClientConfigurationType,
  SectionKey,
  SectionMode,
  TrackerKey,
  WeekdayIndex,
} from "../../../types/clientConfig";
import { ConfigSwitch } from "../../../views/athlete-portal/profile/components/ClientConfiguration/ConfigSwitch";
import { clientConfigRoleLabels } from "../../../views/athlete-portal/profile/components/ClientConfiguration/i18n";
import { SectionRadioGroups } from "../../../views/athlete-portal/profile/components/ClientConfiguration/SectionRadioGroups";
import { SectionToggleCard } from "../../../views/athlete-portal/profile/components/ClientConfiguration/SectionToggleCard";
import {
  NOT_DEFINED_LENS,
  TemplateInsightsLensSelect,
} from "../../../views/athlete-portal/profile/components/ClientConfiguration/TemplateInsightsLensSelect";
import { TrackingItems } from "../../../views/athlete-portal/profile/components/ClientConfiguration/TrackingItems";
import { useVisibleSections } from "../../../views/athlete-portal/profile/components/ClientConfiguration/useVisibleSections";
import {
  type ClientConfigWidgetAvailability,
  TRACKER_KEYS,
  withReadinessForced,
} from "../../../views/athlete-portal/profile/components/ClientConfiguration/utils";
import Modal from "../../modal-new";
import SvgIcon from "../../SvgIcon";

const BLANK_CONFIG: ClientConfigurationType = {
  sections: {
    training: { mode: "coach", clientOptIn: false, order: 0 },
    nutrition: { mode: "coach", clientOptIn: false, order: 1 },
    mealPlans: { mode: "coach", clientOptIn: false, order: 2 },
    dailyAccountabilities: { mode: "coach", clientOptIn: false, order: 3 },
    goals: { mode: "coach", clientOptIn: false, order: 4 },
    femaleCycleTracking: { mode: "hidden", clientOptIn: false, order: 5 },
    yourPlans: { mode: "hidden", clientOptIn: false, order: 7 },
    tracking: {
      order: 6,
      items: Object.fromEntries(
        TRACKER_KEYS.map((key, idx) => [
          key,
          { coachRequired: false, clientEnabled: false, order: idx },
        ])
      ),
      mode: "hidden",
    },
  },
};

function AddClientConfigurationTemplate({
  templateId,
}: {
  templateId?: string;
}) {
  const { t } = useTranslation(["athlete-portal", "common"]);
  const { locals } = useRillContext();
  const {
    activeUserToRequest,
    clientConfigurationTemplates = [],
    clientConfigWidgetAvailability,
    activeUser,
    organizationOwner,
  } = locals as {
    activeUserToRequest: { referAsClients?: boolean };
    clientConfigurationTemplates?: ClientConfigurationType[];
    clientConfigWidgetAvailability?: ClientConfigWidgetAvailability;
    activeUser?: { defaultInsightsLens?: string | null };
    organizationOwner?: { defaultInsightsLens?: string | null };
  };
  const coachDefaultLens = getEffectiveDefaultInsightsLens(
    activeUser,
    organizationOwner
  );
  const referAsClients = activeUserToRequest?.referAsClients ?? false;
  const roleLabels = clientConfigRoleLabels(t, referAsClients);

  const template = useMemo(
    () =>
      templateId
        ? ((clientConfigurationTemplates as ClientConfigurationType[]).find(
            (template) => String(template._id) === String(templateId)
          ) ?? null)
        : null,
    [templateId, clientConfigurationTemplates]
  );

  const [name, setName] = useState<string>(template?.templateName ?? "");
  const [asDefault, setAsDefault] = useState<boolean>(
    template?.isDefault ?? false
  );
  const [stateConfig, setStateConfig] = useState<ClientConfigurationType>(
    template ? cloneDeep(template) : cloneDeep(BLANK_CONFIG)
  );
  // Empty string means "not defined", which only exists for templates saved without a lens
  // before the feature. A new template starts on the coach's current default (explicit and
  // visible); an existing one shows exactly what is stored.
  const allowNotDefinedLens =
    Boolean(template) && !template?.defaultInsightsLens;
  const [lens, setLens] = useState<string>(
    template
      ? (template.defaultInsightsLens ?? NOT_DEFINED_LENS)
      : coachDefaultLens
  );

  const {
    isTrainingVisible,
    isGoalsVisible,
    isYourPlansVisible,
    isFemaleCycleEnabled,
    activeTrackerCount,
    isTrackingEnabled,
  } = useVisibleSections({ clientConfig: stateConfig });

  const setSectionMode = useCallback((key: SectionKey, mode: SectionMode) => {
    setStateConfig((prev: ClientConfigurationType) => {
      const next = cloneDeep(prev);
      if (!next.sections[key]) {
        next.sections[key] = { clientOptIn: false, order: 0, mode };
      } else {
        next.sections[key].mode = mode;
      }
      return next;
    });
  }, []);

  const setTrackerCoachRequired = useCallback(
    (key: TrackerKey, newStatus: boolean) => {
      setStateConfig((prev: ClientConfigurationType) => {
        const next = cloneDeep(prev);
        if (!next.sections.tracking.items[key]) {
          next.sections.tracking.items[key] = {
            coachRequired: false,
            clientEnabled: false,
            order: TRACKER_KEYS.indexOf(key),
          };
        }
        const item = next.sections.tracking.items[key];
        if (newStatus) {
          item.coachRequired = true;
          item.clientEnabled = false;
          if (key !== "readiness" && key !== "sessionRpe") {
            const currentSchedule = item.schedule;
            if (!currentSchedule?.length) item.schedule = [1, 3, 5];
          }
        } else {
          item.coachRequired = false;
          item.clientEnabled = false;
        }
        return next;
      });
    },
    []
  );

  /**
   * Sets the forced status for the readiness tracker.
   * @param newStatus - The new status for the readiness tracker.
   */
  const setReadinessForced = useCallback((newStatus: boolean) => {
    setStateConfig((prev: ClientConfigurationType) =>
      withReadinessForced(prev, newStatus)
    );
  }, []);

  const setTrackerSchedule = useCallback(
    (key: TrackerKey, schedule: WeekdayIndex[]) => {
      if (key === "readiness" || key === "sessionRpe") return;
      setStateConfig((prev: ClientConfigurationType) => {
        const next = cloneDeep(prev);
        if (!next.sections.tracking.items[key]) return next;
        next.sections.tracking.items[key].schedule = schedule;
        return next;
      });
    },
    []
  );

  const setBodyMeasurementsParts = useCallback((parts: BodyPartKey[]) => {
    setStateConfig((prev: ClientConfigurationType) => {
      const next = cloneDeep(prev);
      if (!next.sections.tracking.items.bodyMeasurements) return next;
      next.sections.tracking.items.bodyMeasurements.bodyParts = parts;
      return next;
    });
  }, []);

  const handleSave = useCallback(() => {
    if (!name.trim()) return;
    if (templateId) {
      submit("/actions/client-configuration/update-template", {
        method: "POST",
        body: {
          templateId: templateId,
          templateName: name.trim(),
          sections: stateConfig.sections,
          isDefault: asDefault,
          defaultInsightsLens: lens,
        },
      });
    } else {
      submit("/actions/client-configuration/create-template", {
        method: "POST",
        body: {
          templateName: name.trim(),
          sections: stateConfig.sections,
          isDefault: asDefault,
          defaultInsightsLens: lens,
        },
      });
    }
  }, [name, stateConfig.sections, templateId, asDefault, lens]);

  return (
    <Modal
      className="add-client-configuration-template-modal"
      toClear={["currentConfigTemplateId"]}
      title={t("clientConfiguration.addTemplateModal.title")}
      customTitle={
        <input
          type="text"
          className="input-template-title"
          placeholder={t(
            "clientConfiguration.addTemplateModal.namePlaceholder"
          )}
          value={name}
          name="template-name"
          onChange={(e) => setName(e.target.value)}
        />
      }
    >
      <div className="body">
        <section
          className="athlete-profile client-configuration"
          aria-label={t("clientConfiguration.addTemplateModal.ariaLabel")}
        >
          <section className="client-config-sections">
            <SectionToggleCard
              variant="editable"
              icon="🏋"
              titleKey="clientConfiguration.sections.training"
              hiddenHintKey="clientConfiguration.copy.training.hiddenHint"
              visibleHintKey="clientConfiguration.copy.training.activeHint"
              hiddenHintClassName="chip warning"
              roleLabels={roleLabels}
              visible={isTrainingVisible}
              switchId="cc-template-training"
              ariaLabelKey="clientConfiguration.aria.showTraining"
              onChange={(value) => {
                setStateConfig((prev: ClientConfigurationType) => {
                  const next = cloneDeep(prev);
                  if (!next.sections.training) {
                    next.sections.training = {
                      mode: "coach",
                      clientOptIn: false,
                      order: 0,
                    };
                  }
                  next.sections.training.mode = value ? "coach" : "hidden";
                  return next;
                });
              }}
            />

            <SectionRadioGroups
              roleLabels={roleLabels}
              sectionWidgetAvailability={clientConfigWidgetAvailability}
              getSectionMode={(key) => stateConfig?.sections?.[key]?.mode}
              onSectionModeChange={setSectionMode}
              idPrefix="template"
            />

            <SectionToggleCard
              variant="editable"
              icon="🎯"
              titleKey="clientConfiguration.sections.goals"
              hiddenHintKey="clientConfiguration.copy.goals.hiddenHint"
              visibleHintKey="clientConfiguration.copy.goals.visibleHint"
              roleLabels={roleLabels}
              visible={isGoalsVisible}
              switchId="template-goals"
              ariaLabelKey="clientConfiguration.aria.showGoals"
              onChange={(value) => {
                setStateConfig((prev: ClientConfigurationType) => {
                  const next = cloneDeep(prev);
                  if (!next.sections.goals) {
                    next.sections.goals = {
                      mode: "coach",
                      clientOptIn: false,
                      order: 4,
                    };
                  }
                  next.sections.goals.mode = value ? "coach" : "hidden";
                  return next;
                });
              }}
            />

            <div className="client-config-card tracking">
              <div className="client-config-card__header">
                <div className="client-config-card__left">
                  <div className="client-config-card__title">
                    <span>
                      📊{t("clientConfiguration.sections.trackingRequirements")}
                    </span>
                    {isTrackingEnabled && (
                      <span className="chip info">
                        {t("clientConfiguration.activeTrackers", {
                          count: activeTrackerCount,
                        })}
                      </span>
                    )}
                  </div>
                </div>
                <div className="client-config-card__right">
                  <ConfigSwitch
                    id="cc-template-tracking-enabled"
                    ariaLabel={t(
                      "clientConfiguration.aria.showTracking",
                      roleLabels
                    )}
                    checked={isTrackingEnabled}
                    onChange={(value: boolean) => {
                      setStateConfig((prev: ClientConfigurationType) => {
                        const next = cloneDeep(prev);
                        if (!next.sections.tracking) {
                          next.sections.tracking = {
                            mode: "hidden",
                            clientOptIn: false,
                            order: 6,
                            items: {},
                          };
                        }
                        // Only change mode; preserve all individual tracker settings.
                        next.sections.tracking.mode = value
                          ? "coach"
                          : "hidden";
                        return next;
                      });
                    }}
                  />
                </div>
              </div>
              {isTrackingEnabled && (
                <>
                  <p className="p6 client-config-card__intro">
                    {t("clientConfiguration.trackingIntro", roleLabels)}
                  </p>
                  <TrackingItems
                    roleLabels={roleLabels}
                    currentConfig={stateConfig}
                    onTrackerClick={setTrackerCoachRequired}
                    onForcedChange={setReadinessForced}
                    onScheduleChanged={setTrackerSchedule}
                    onBodyPartsChanged={setBodyMeasurementsParts}
                  />
                </>
              )}
            </div>

            <SectionToggleCard
              variant="editable"
              icon="🩸"
              titleKey="clientConfiguration.sections.femaleCycle"
              hiddenHintKey="clientConfiguration.copy.femaleCycleTracking.hiddenHint"
              visibleHintKey="clientConfiguration.copy.femaleCycleTracking.visibleHint"
              roleLabels={roleLabels}
              visible={isFemaleCycleEnabled}
              switchId="cc-template-female-cycle"
              ariaLabelKey="clientConfiguration.aria.femaleCycle"
              onChange={(value) => {
                setStateConfig((prev: ClientConfigurationType) => {
                  const next = cloneDeep(prev);
                  if (!next.sections.femaleCycleTracking) {
                    next.sections.femaleCycleTracking = {
                      mode: "hidden",
                      clientOptIn: false,
                      order: 5,
                    };
                  }
                  next.sections.femaleCycleTracking.mode = value
                    ? "client"
                    : "hidden";
                  return next;
                });
              }}
            />

            <SectionToggleCard
              variant="editable"
              icon="📅"
              titleKey="clientConfiguration.sections.yourPlans"
              hiddenHintKey="clientConfiguration.copy.yourPlans.hiddenHint"
              visibleHintKey="clientConfiguration.copy.yourPlans.visibleHint"
              roleLabels={roleLabels}
              visible={isYourPlansVisible}
              switchId="cc-template-your-plans"
              ariaLabelKey="clientConfiguration.aria.yourPlans"
              onChange={(value) => {
                setStateConfig((prev: ClientConfigurationType) => {
                  const next = cloneDeep(prev);
                  if (!next.sections.yourPlans) {
                    next.sections.yourPlans = {
                      mode: "hidden",
                      clientOptIn: false,
                      order: 7,
                    };
                  }
                  next.sections.yourPlans.mode = value ? "client" : "hidden";
                  return next;
                });
              }}
            />

            <TemplateInsightsLensSelect
              value={lens}
              onChange={setLens}
              allowNotDefined={allowNotDefinedLens}
            />
          </section>
        </section>
      </div>

      <footer className="client-config-template-footer client-config-footer-buttons">
        <button type="button" onClick={() => setAsDefault(!asDefault)}>
          <SvgIcon
            src={`/img/new-icons/${asDefault ? "" : "un"}checked-square.svg`}
            noFilterOnLight={asDefault}
          />
          <p className="p4">
            {t("clientConfiguration.addTemplateModal.setAsDefault")}
          </p>
        </button>
        <button type="button" onClick={handleSave} data-button-type="primary">
          {t("common:save")}
        </button>
      </footer>
    </Modal>
  );
}

export default AddClientConfigurationTemplate;
